import React from 'react';

/**
 * Shared chrome for every /staff page — the RUBY THEME, which is the
 * house standard for ALL new super-admin development (user decision
 * 2026-08-26): black header bar + ruby gradient underline + LIGHT stone
 * content area with white cards and ruby (#DC143C) primary actions.
 * NOT the legacy purple admin-primary dark chrome.
 *
 * Mirrors pages/inventory/InventoryChrome.tsx (the original of this
 * pattern) with a Staff breadcrumb.
 */

export const RUBY = '#DC143C';
export const RUBY_DEEP = '#9F1239';

export const StaffHeader: React.FC<{
  icon: React.FC<any>;
  title: string;
  subtitle?: string;
  /** Breadcrumb section name; defaults to Staff. */
  section?: string;
  actions?: React.ReactNode;
  /** Sub-tab bar rendered INSIDE the black header, under the title row, above the ruby line. */
  tabs?: React.ReactNode;
}> = ({ icon: Icon, title, subtitle, section = 'Staff', actions, tabs }) => (
  <div className="bg-black">
    <div className="px-4 md:px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center shrink-0">
          <Icon size={20} className="text-white" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
            <span>{section}</span>
            <span className="opacity-50">&rsaquo;</span>
            <span className="text-gray-300">{title}</span>
          </div>
          <h1 className="text-base md:text-lg font-semibold text-white truncate">{title}</h1>
          {subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
    {tabs && <div className="px-4 md:px-6 pb-2">{tabs}</div>}
    <div style={{ background: `linear-gradient(90deg, #e8365a, ${RUBY}, ${RUBY_DEEP})`, height: '3px' }} />
  </div>
);

/** Ruby-tinted pill sub-tabs — render only on a black background. */
export const StaffSubtabs: React.FC<{
  tabs: { id: string; label: string; badge?: number }[];
  active: string;
  onChange: (id: string) => void;
}> = ({ tabs, active, onChange }) => (
  <div className="flex items-center gap-1 overflow-x-auto">
    {tabs.map((t) => {
      const on = active === t.id;
      return (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition flex items-center gap-1.5 ${
            on ? '' : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
          /*
            44px, NOT 38. The platform rule is a 44px hit area for anything a
            person taps (CLAUDE.md), and these tabs were 38 -- which went
            unnoticed while every tab was a convenience beside the real controls.
            It stopped being true when `awaiting_owner_approval` moved out of its
            own 44px pill and INTO this strip: the owner's way to their own
            approval pile is now one of these buttons.

            Inline rather than `min-h-[44px]` so the measurement is readable
            without a compiled stylesheet -- jsdom sees no Tailwind, and a rule
            no test can check is a rule that drifts back.
          */
          style={on
            ? { minHeight: 44, background: 'rgba(220,20,60,0.16)', border: '1px solid rgba(220,20,60,0.5)', color: '#fda4af' }
            : { minHeight: 44, border: '1px solid transparent' }}
        >
          {t.label}
          {typeof t.badge === 'number' && t.badge > 0 && (
            <span
              className="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
              style={{ background: RUBY }}
            >
              {t.badge}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

/** Standard page shell: black header, ruby underline, light content area. */
export const StaffPage: React.FC<{
  icon: React.FC<any>;
  title: string;
  subtitle?: string;
  section?: string;
  actions?: React.ReactNode;
  tabs?: React.ReactNode;
  children: React.ReactNode;
  /**
   * Drop the 1400px reading cap and run edge to edge.
   *
   * Mirrors `InventoryPage`'s prop of the same name, deliberately including its
   * reasoning: the cap is there for pages that are a DOCUMENT, where a line of
   * prose gets harder to read the wider it runs. It is wrong for a page that is
   * a WORKSPACE -- a dense table whose columns are already fighting for room,
   * where the cap just leaves a stone-coloured margin either side and makes the
   * page look narrower than every other screen in the admin.
   */
  wide?: boolean;
  /** Content-area background class; defaults to the house `bg-stone-100`. */
  bodyClassName?: string;
}> = ({ icon, title, subtitle, section, actions, tabs, children, wide, bodyClassName = 'bg-stone-100' }) => (
  /*
    Flex column, body `flex-1` — NOT `min-h-[calc(100vh-72px)]`. The header is
    72px only with no subtitle and no tabs; with either it is 90-140px, so a
    fixed calc made header + body taller than <main> and every page, even an
    empty one, grew a scrollbar for the difference.
  */
  <div className="min-h-screen bg-black -m-3 md:-m-6 flex flex-col">
    <StaffHeader icon={icon} title={title} subtitle={subtitle} section={section} actions={actions} tabs={tabs} />
    <div className={`${bodyClassName} flex-1 text-stone-800`}>
      <div className={`px-4 md:px-6 py-4 md:py-6 ${wide ? 'w-full' : 'max-w-[1400px] mx-auto'}`}>
        {children}
      </div>
    </div>
  </div>
);
